<template>
    <li :class="root ? 'root' : checked === false ? 'excluded' : ''">
        <div class="d-inline">
            <span v-if="root" class="border-bottom-primary" style="border-width: 2px;">
                {{ name }}
            </span>
            <BFormCheckbox
                v-else
                class="d-inline"
                v-model="checked"
                :disabled="false === parentChecked || readonly"
                @change="onCheckedChange"
            >
                <span :class="{'readonly': readonly && parentChecked, 'excluded': false === parentChecked || !checked}">
                    {{ name }}
                </span>
            </BFormCheckbox>
        </div>

        <div :class="{'info': true, 'no-children': hasChildren}">
            <div v-if="article.isReference">
                <b-row>
                    <b-col md="4" sm="5" lg="3">{{ identifierType }}</b-col>
                    <b-col md="8" sm="7" lg="9">{{ article.primaryIdentifier.value }}</b-col>
                </b-row>
            </div>
            <div v-else>
                <Overlay :show="showLoading">
                    <b-row v-if="showPrimaryIdentifier" :class="{'input-bg-warning': isIdentifierUndefined}">
                        <b-col md="4" sm="5" lg="3">
                            {{ isIdentifierUndefined ? $t('Component(Label):primary-key') : identifierType }}
                        </b-col>
                        <b-col md="8" sm="7" lg="9">
                            <span v-if="isIdentifierUndefined">
                                {{ $t('Page(SCIPPreview):Component(WarningText):primary-identifier-missing') }}
                                <b-link target="_blank" :to="{name: 'product-card', params: {id: article.id}}">
                                    {{ $t('Page(SCIPPreview):Component(Link):goto-product') }}
                                </b-link>
                            </span>
                            <span v-else>{{ article.primaryIdentifier.value }}</span>
                        </b-col>
                    </b-row>
                    <b-row class="mt-50" v-if="isOtherIdentifiersSelectorVisible">
                        <b-col md="4" sm="5" lg="3">{{ $t('Component(Label):other-identifiers') }}</b-col>
                        <b-col md="8" sm="7" lg="9">
                            <OtherIdentifiersSelector
                                :disabled="!checked || readonly"
                                :options="article.availableIdentifiers"
                                :selected="article.scipSettings.otherIdentifiers"
                                :translations="identifiersTranslations"
                                @input="onOtherIdentifiersChange"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mt-50">
                        <b-col md="4" sm="5" lg="3" class="d-flex align-items-center">{{ $t('Component(Label):scip-name') }}</b-col>
                        <b-col md="8" sm="7" lg="9">
                            <b-form-input
                                id="name"
                                v-model="article.scipName"
                                :class="{'input-bg-warning': !article.scipName}"
                                @input="onScipNameInput"
                                :disabled="readonly"
                                :placeholder="$t('Component(Placeholder):scip-name')"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mt-50" v-if="isOtherNamesSelectorVisible">
                        <b-col md="4" sm="5" lg="3">{{ $t('Component(Label):other-names') }}</b-col>
                        <b-col md="8" sm="7" lg="9">
                            <OtherNamesSelector
                                :disabled="!checked || readonly"
                                :options="article.availableNames"
                                :selected="article.scipSettings.otherNames"
                                :translations="namesTranslations"
                                @input="onOtherNamesChange"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mt-50">
                        <b-col md="4" sm="5" lg="3" class="d-flex align-items-center">
                            {{ $t('Component(Label):production-flag') }}
                        </b-col>
                        <b-col md="8" sm="7" lg="9">
                            <ProductionFlag
                                :disabled="!checked || readonly"
                                :selected="article.scipSettings.productionFlag"
                                @input="onProductionFlagChange"
                            />
                        </b-col>
                    </b-row>
                    <b-row class="mt-50" :class="{'input-bg-warning': !hasTaricCodes}">
                        <b-col md="4" sm="5" lg="3">{{ $t('Component(Text):taric-codes') }}</b-col>
                        <b-col md="8" sm="7" lg="9">
                            <div class="category-title" v-for="category in article.categories" :title="category.title">{{ category.title }}</div>
                            <div v-if="!hasTaricCodes">
                                {{ $t('Page(SCIPPreview):Component(WarningText):taric-codes-missing') }}
                                <b-link target="_blank" :to="{name: 'product-card', params: {id: article.id}}">
                                    {{ $t('Page(SCIPPreview):Component(Link):goto-product') }}
                                </b-link>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row v-if="article.concernElements.length > 0" class="mt-50">
                        <b-col md="4" sm="5" lg="3">{{ $t('Component(Label):concern-elements') }}</b-col>
                        <b-col md="8" sm="7" lg="9">
                            <ConcernElement
                                v-for="ce in article.concernElements"
                                :id="article.id"
                                :ce="ce"
                            />
                        </b-col>
                    </b-row>
                    <b-row v-if="article.hasNotDeclaredSubstances" class="input-bg-warning mt-50">
                        <b-col>{{ $t('Page(SCIPPreview):Component(WarningText):has-not-declared-substances') }}</b-col>
                    </b-row>
                    <b-row v-if="showSafeUseMissingMessage" class="input-bg-warning mt-50">
                        <b-col>{{ $t('Page(SCIPPreview):Component(WarningText):safe-use-instructions-missing') }}</b-col>
                    </b-row>
                    <b-row v-else-if="false === article.isSafeUserApplicable" class="mt-50">
                        <b-col>{{ $t('Component(Text):safe-use-applicability') }}</b-col>
                    </b-row>
                    <div v-if="article.safeUseInstructions">
                        <b-row class="mt-50" v-if="article.safeUseInstructions.safeUseInstructions.length > 0">
                            <b-col md="4" sm="5" lg="3">
                                {{ $t('Component(Label):safe-use-instructions') }}
                            </b-col>
                            <b-col md="8" sm="7" lg="9">
                                <div v-for="instruction in article.safeUseInstructions.safeUseInstructions">
                                    <div class="safe-use" :title="instruction">{{ instruction }}</div>
                                </div>
                            </b-col>
                        </b-row>
                        <b-row class="mt-50" v-if="article.safeUseInstructions.disassemblingInstructionAttachments.length > 0">
                            <b-col md="4" sm="5" lg="3">
                                {{ $t('Component(Label):dissembling-instructions') }}
                            </b-col>
                            <b-col md="8" sm="7" lg="9">
                                {{ article.safeUseInstructions.disassemblingInstructionAttachments.map(a => a.filename).join(', ') }}
                            </b-col>
                        </b-row>
                    </div>

                </Overlay>
            </div>
        </div>

        <ul v-if="hasChildren">
            <ArticleTreeItem
                v-for="(child) in article.children"
                :article="child"
                :root-article="rootArticle"
                :parent="article"
                :readonly="readonly"
                :parent-checked="checked"
                :names-translations="namesTranslations"
                :identifiers-translations="identifiersTranslations"
                @change="emitChange"
            />
        </ul>
    </li>
</template>

<script>
import ConcernElement from "@/views/product/components/scip-notification/ConcernElement.vue";
import ProductionFlag from "@/views/product/components/scip-notification/ProductionFlag.vue";
import {BFormCheckbox,BFormGroup, BFormInput,BRow, BCol, BLink} from 'bootstrap-vue'
import {ProductIdentifiers, ProductNames, ProductType, setNameOrId} from "@/libs/product";
import {CountrySelector} from "@core/components/supply-chain";
import {ProductProvider} from "@core/domain/product";
import OtherNamesSelector from "@/views/product/components/scip-notification/OtherNamesSelector.vue";
import Overlay from "@core/components/common/Overlay.vue";
import OtherIdentifiersSelector from "@/views/product/components/scip-notification/OtherIdentifiersSelector.vue";
export default {
    name: 'ArticleTreeItem',
    components: {
        OtherIdentifiersSelector,
        Overlay,
        OtherNamesSelector,
        BFormCheckbox,BFormGroup, BFormInput, CountrySelector, BRow, BCol, BLink, ProductionFlag, ConcernElement},
    props: {
        readonly: {
            default: false
        },
        root: {
            type: Boolean,
            default: false
        },
        rootArticle: {
            required: true,
            type: Object
        },
        article: {
            required: true,
            type: Object
        },
        parent: {
            type: Object
        },
        parentChecked: {
            type: Boolean,
            default: true
        },
        identifiersTranslations: {
            required: true
        },
        namesTranslations: {
            required: true
        }
    },
    data() {
        return {
            checked: this.parentChecked && !this.article.isExcluded,
            cardSaveTimeout: null,
            scipSettingsSaveTimeout: null,
            showLoading: false
        };
    },
    methods: {
        onCheckedChange() {
            if (this.readonly) {
                return;
            }

            const productDomain = ProductProvider(this.rootArticle.id);
            this.showLoading = true;

            if (this.checked) {
                productDomain
                    .unsetScipNotificationExclusion(this.article.id, this.parent.id)
                    .then(() => {
                        this.article.isExcluded = false;
                        this.emitChange();
                    })
                    .finally(() => this.showLoading = false)
                ;
            } else {
                productDomain
                    .excludeFromScipNotification(this.article.id, this.parent.id)
                    .then(() => {
                        this.article.isExcluded = true;
                        this.emitChange();
                    })
                    .finally(() => this.showLoading = false)
                ;
            }
        },
        onScipNameInput() {
            if (this.readonly) {
                return;
            }
            if (this.article.scipName) {
                clearTimeout(this.cardSaveTimeout);

                this.cardSaveTimeout = setTimeout(() => {
                    this.showLoading = true;
                    const product = ProductProvider(this.article.id);
                    product
                        .getCard()
                        .then((card) => {
                            setNameOrId(card.names, ProductNames.SCIP_NAME, this.article.scipName);
                            return product.saveCard();
                        })
                        .then(this.emitChange)
                        .finally(() => this.showLoading = false)
                    ;
                }, 600)
            }
        },
        onProductionFlagChange(flag) {
            this.article.scipSettings.productionFlag = flag.id;
            this.saveScipSettings()
        },
        onOtherNamesChange(otherNames) {
            this.article.scipSettings.otherNames = otherNames;
            this.saveScipSettings();
        },

        onOtherIdentifiersChange(identifiers) {
            this.article.scipSettings.otherIdentifiers = identifiers;
            this.saveScipSettings();
        },

        saveScipSettings() {
            if (this.readonly) {
                return;
            }
            clearTimeout(this.scipSettingsSaveTimeout);

            this.scipSettingsSaveTimeout = setTimeout(() => {
                const product = ProductProvider(this.article.id);
                this.showLoading = true;
                product
                    .saveScipSettings(this.article.scipSettings)
                    .then(this.emitChange)
                    .finally(() => this.showLoading = false)
                ;
            }, 600);
        },
        emitChange() {
            this.$emit('change')
        }
    },
    computed: {
        name() {
            return this.article.scipName || this.article.primaryName.value;
        },
        showSafeUseMissingMessage() {
            if (false === this.article.isSafeUserApplicable) {
                return false;
            }

            return null === this.article.safeUseInstructions || 0 === this.article.safeUseInstructions.safeUseInstructions.length;
        },
        identifierType() {
            if (ProductIdentifiers.OTHER === this.article.primaryIdentifier.type) {
                return this.identifiersTranslations[this.article.primaryIdentifier.type] + ' (' + this.article.primaryIdentifier.valueType + ')';
            }
            return this.identifiersTranslations[this.article.primaryIdentifier.type];
        },
        isIdentifierUndefined() {
            return ProductIdentifiers.UNDEFINED === this.article.primaryIdentifier.type;
        },
        isOtherNamesSelectorVisible() {
            return ProductType.OWN === this.article.type && this.article.availableNames.length > 0;
        },
        isOtherIdentifiersSelectorVisible() {
            return ProductType.OWN === this.article.type && this.article.availableIdentifiers.length > 0;
        },
        hasChildren() {
            return this.article.children.length > 0;
        },
        showPrimaryIdentifier() {
            return !(this.article.type === ProductType.PURCHASED && this.article.primaryIdentifier.type === ProductIdentifiers.UUID);
        },
        hasTaricCodes() {
            return this.article.categories.length > 0;
        }
    },
    watch: {
        parentChecked() {
            if (false === this.parentChecked) {
                this.checked = false;
            } else if (true === this.parentChecked && this.checked === false && !this.article.isExcluded) {
                this.checked = true;
            }
        }
    }
}
</script>

<style scoped lang="scss">
$excludedBorderColor: #f6f6f6;
.readonly:not(.excluded) {
    color: #484552 !important;
}
ul.tree, ul.tree ul:not(.concern-elements) {
    list-style: none;
    margin: 0;
    padding: 0;
}
ul.tree ul {
    margin-left: 10px;
}
ul.tree ul:not(.concern-elements)  ul {
    margin-left: 20px !important;
}
ul.tree > li.root > ul {
    padding-left: 7px;
}
ul.tree li:not(.concern-element-item) {
    margin: 0;
    padding: 0 7px;
    line-height: 30px;
    font-weight: bold;
    border-left:2px solid #F69161;
}
ul.tree li:last-child {
    border-left:none;
}
ul.tree li:not(.root):not(.concern-element-item):before {
    position:relative;
    top:-0.34em;
    height:1.1em;
    width:12px;
    color:white;
    border-bottom:2px solid #F69161;
    content:"";
    display:inline-block;
    left:-7px;
}
ul.tree li.root div.info {
    border-left:2px solid #F69161;
    margin-left: 7px;
    padding-left: 16px;
    font-weight: normal;

    .category-title, .safe-use {
        overflow: hidden;
        white-space: nowrap;
    }
}
ul.tree li.excluded div.info {
    border-color: $excludedBorderColor;
    color: #b8c2cc;

    input {
        color: #b8c2cc !important;
    }
}
ul.tree li:not(.root) div.info {
    margin-left: 20px;
    padding-left: 16px;
}
ul.tree li.root div.info:last-child {
    border: none;
    padding-left: 17px;
}
ul.tree li:not(.concern-element-item):last-child:before {
    border-left:2px solid #F69161;
}
ul.tree li.excluded > ul li.excluded, ul.tree li.excluded:not(.root):not(.concern-element-item):before {
//ul.tree li.excluded, ul.tree li.excluded:not(.root):not(.concern-element-item):before {
    border-color: $excludedBorderColor;
}
ul.tree li.excluded > ul li:last-child:before {
    border-color: $excludedBorderColor;
}
</style>
